import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useCreateOrUpdateInvoiceSettingsMutation } from "store/apis/invoices";
import { InvoiceSettingsType } from "types/Models/invoice";
import { emailValidationRegex } from "constants/emailValidationRegex";
import { FormikForm } from "components/FormikForm/FormikForm";
import { BackendError } from "types/utils/error";
type EmailPreferencesPayload = {
  ccEmails: string[];
};
export const EmailPreferencesModal = ({
  isOpen,
  close,
  invoiceSetting,
}: {
  isOpen: boolean;
  close: () => void;
  invoiceSetting?: InvoiceSettingsType | null;
}) => {
  const [createOrUpdateInvoiceSetting, { isLoading }] =
    useCreateOrUpdateInvoiceSettingsMutation();
  const { successToast, alertToast, info: infoToast } = useToast();
  const group = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const initialValue: EmailPreferencesPayload = {
    ccEmails: invoiceSetting?.cc_recipient_emails || [],
  };
  const isValidEmail = (email: string) => {
    return emailValidationRegex.test(email);
  };
  const onEmailPreferencesSave = async (values: EmailPreferencesPayload) => {
    if (!group?.uuid || !entityId) {
      return;
    }
    try {
      await createOrUpdateInvoiceSetting({
        groupId: group.uuid,
        entityId: entityId,
        payload: {
          cc_recipient_emails: values.ccEmails,
        },
      }).unwrap();
      successToast({
        message: "Email preferences updated successfully",
      });
      close();
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };
  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay asChild>
        <FormikForm
          initialValues={initialValue}
          onSubmit={onEmailPreferencesSave}
          enableReinitialize
        >
          {({ submitForm, values, setFieldValue }) => {
            const ccEmailOptions =
              values.ccEmails?.map((email: string) => ({
                value: email,
                label: email,
              })) || [];
            return (
              <>
                <Modal.Header>
                  <Modal.Title>Email preferences</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body>
                  <Combobox
                    label="Add team email"
                    name="ccEmails"
                    size="small"
                    creatable
                    isMulti
                    placeholder="Add email address"
                    options={ccEmailOptions}
                    value={ccEmailOptions}
                    isLoading={isLoading}
                    menuPortalTarget={document.body}
                    onChange={(selectedOptions) => {
                      const emails = selectedOptions
                        ? (selectedOptions as any[]).map((opt) => opt.value)
                        : [];
                      if (emails.length === 0) {
                        infoToast({
                          message: "At least one email is required",
                        });
                        return;
                      }
                      setFieldValue("ccEmails", emails);
                    }}
                    onCreateOption={(inputValue) => {
                      const trimmedEmail = inputValue.trim();
                      if (!isValidEmail(trimmedEmail)) {
                        alertToast({
                          message: "Please enter a valid email address",
                        });
                        return;
                      }
                      const newEmails = [
                        ...(values.ccEmails || []),
                        trimmedEmail,
                      ];
                      setFieldValue("ccEmails", newEmails);
                    }}
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Button type="button" onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={isLoading}
                    customType="primary"
                    onClick={submitForm}
                  >
                    Save
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
